import React, { useEffect, useState } from "react";
import { api } from "../api.js";
import SignatureAdopt from "../onboarding/SignatureAdopt.jsx";
import ClientDocRenderer from "./ClientDocRenderer.jsx";
import { fmtTime } from "../onboarding/helpers.js";

// Client 360 → "E-Signatures": send admission / discharge documents to the client or
// family representative, complete Jybek's parts, and approve them into the client file.
const STATUS = {
  draft: ["Draft — not sent", "bg-gray-100 text-gray-700"],
  sent: ["Sent — waiting for signer", "bg-blue-50 text-blue-700"],
  in_progress: ["Signer in progress", "bg-blue-50 text-blue-700"],
  signed: ["Signed — needs Jybek approval", "bg-amber-50 text-amber-800"],
  approved: ["Approved & filed", "bg-green-50 text-green-700"],
  cancelled: ["Cancelled", "bg-red-50 text-red-700"],
  paper_requested: ["Signer asked for paper", "bg-purple-50 text-purple-700"],
};
const Pill = ({ s }) => { const [t, c] = STATUS[s] || [s, "bg-gray-100"]; return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c}`}>{t}</span>; };
const btn = "px-3 py-2 rounded-lg text-sm font-semibold";
const primary = `${btn} bg-[#154E93] text-white disabled:opacity-50`;
const ghost = `${btn} border text-gray-700 bg-white`;

function NewRequest({ clientId, info, onCreated, onCancel }) {
  const [kind, setKind] = useState("admission");
  const tpls = info.templates.filter((t) => t.kind === kind);
  const [docs, setDocs] = useState(() => info.templates.filter((t) => t.kind === "admission" && !t.optional).map((t) => t.key));
  const d = info.defaults;
  const [who, setWho] = useState(d.agent_name ? "agent" : "client");
  const fromWho = (w) => (w === "agent"
    ? { signer_name: d.agent_name || "", signer_relationship: d.agent_relationship || "", signer_email: d.agent_email || "", signer_phone: d.agent_phone || "" }
    : { signer_name: d.client_name || "", signer_relationship: "Client", signer_email: d.client_email || "", signer_phone: d.client_phone || "" });
  const [f, setF] = useState(() => fromWho(who));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pickKind = (k) => { setKind(k); setDocs(info.templates.filter((t) => t.kind === k && !t.optional).map((t) => t.key)); };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="bg-white border rounded-xl p-5 space-y-4">
      <h3 className="font-bold text-lg text-[#1B3A6B]">New signing request</h3>
      <div className="flex gap-2">
        {[["admission", "Admission documents"], ["discharge", "Discharge letter"]].map(([k, l]) => <button key={k} onClick={() => pickKind(k)} className={kind === k ? primary : ghost}>{l}</button>)}
      </div>
      {kind === "discharge" && !info.has_discharge_record && <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">No discharge record yet on the <b>Discharge &amp; Transition</b> tab. You can still send the letter — fill in the date, reason, summary and instructions on the letter before sending.</div>}
      <div>
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Documents</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {tpls.map((t) => (
            <label key={t.key} className="flex items-start gap-2 border rounded-lg p-2.5 text-sm">
              <input type="checkbox" className="mt-1 accent-[#154E93]" checked={docs.includes(t.key)} onChange={(e) => setDocs(e.target.checked ? [...docs, t.key] : docs.filter((x) => x !== t.key))} />
              <span><b>{t.title}</b>{t.optional ? <span className="text-gray-500"> (only if paying by card / bank)</span> : null}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Who signs</div>
        <div className="flex gap-2 mb-3">
          {[["client", "The client"], ["agent", "Family / authorized representative"]].map(([k, l]) => <button key={k} onClick={() => { setWho(k); setF(fromWho(k)); }} className={who === k ? primary : ghost}>{l}</button>)}
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <label>Full name<input value={f.signer_name} onChange={set("signer_name")} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
          <label>Relationship to client<input value={f.signer_relationship} onChange={set("signer_relationship")} placeholder="e.g. Daughter, POA, Guardian" className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
          <label>Email<input type="email" value={f.signer_email} onChange={set("signer_email")} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
          <label>Mobile (gets the access code by text)<input type="tel" value={f.signer_phone} onChange={set("signer_phone")} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
        </div>
        {who === "agent" && <p className="text-xs text-gray-500 mt-2">Keep a copy of the POA / guardianship papers in the client's Documents tab.</p>}
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <div className="flex gap-2">
        <button disabled={busy} className={primary} onClick={async () => { setBusy(true); setError(""); try { const r = await api.clientEsign.create(clientId, { kind, docs, ...f }); onCreated(r.id); } catch (e) { setError(e.message); } finally { setBusy(false); } }}>Create draft</button>
        <button className={ghost} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function AgencyDoc({ clientId, req, docKey, onChanged }) {
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const load = () => api.clientEsign.doc(clientId, req.id, docKey).then(setP).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [docKey, req.id, req.status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return <div className="text-sm text-gray-500 p-4">{error || "Loading…"}</div>;
  const closed = ["approved", "cancelled", "paper_requested"].includes(req.status);
  const lockedKeys = req.status === "draft" ? [] : p.template.preSend;
  return (
    <div className="border-t bg-slate-50 p-4 space-y-3">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <span className="text-xs text-gray-600">Amber boxes are completed by Jybek. {req.status !== "draft" && p.template.preSend.length ? "Items the signer has already seen are locked." : ""}</span>
        <a href={api.clientEsign.previewUrl(clientId, req.id, docKey)} target="_blank" rel="noreferrer" className={ghost}>Preview PDF</a>
      </div>
      <div className="bg-white border rounded-xl p-4 sm:p-6">
        <ClientDocRenderer template={p.template} data={p.data} role="agency" locked={closed} lockedKeys={lockedKeys} images={p.images} stamps={p.stamps} signerRole={p.signer_role} busyKey={busyKey}
          onChange={async (v) => { setError(""); setP((c) => ({ ...c, data: { ...c.data, ...v } })); try { await api.clientEsign.save(clientId, req.id, docKey, v); await load(); onChanged(); } catch (e) { setError(e.message); load(); } }}
          onStamp={async (k) => { setBusyKey(k); setError(""); try { await api.clientEsign.stamp(clientId, req.id, docKey, k); await load(); onChanged(); } catch (e) { setError(e.message); } finally { setBusyKey(null); } }} />
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
    </div>
  );
}

function RequestDetail({ clientId, rid, onBack, reloadClient }) {
  const [r, setR] = useState(null);
  const [open, setOpen] = useState(null);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");
  const [missing, setMissing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.clientEsign.get(clientId, rid).then(setR).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [rid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return <div className="text-gray-500">{error || "Loading…"}</div>;
  const act = async (fn, after) => { setBusy(true); setError(""); setMissing(null); try { const out = await fn(); if (after) after(out); await load(); } catch (e) { if (e.data?.missing) setMissing(e.data.missing); setError(e.message); } finally { setBusy(false); } };
  const live = ["sent", "in_progress"].includes(r.status);
  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-[#154E93] hover:underline">← All signing requests</button>
      <div className="bg-white border rounded-xl p-5">
        <div className="flex flex-wrap justify-between gap-3">
          <div>
            <div className="text-xs font-bold tracking-[0.15em] text-[#C9A84C]">{r.kind === "discharge" ? "DISCHARGE LETTER" : "ADMISSION DOCUMENTS"} · #{r.id}</div>
            <h3 className="text-xl font-bold text-[#1B3A6B]">{r.signer_name} <span className="text-base font-normal text-gray-500">({r.signer_relationship})</span></h3>
            <div className="text-sm text-gray-600">{[r.signer_email, r.signer_phone].filter(Boolean).join(" · ")}</div>
            <div className="text-xs text-gray-500 mt-1">Created {fmtTime(r.created_at)} by {r.created_by}{r.sent_at ? ` · sent ${fmtTime(r.sent_at)}` : ""}{r.expires_at && r.status !== "approved" ? ` · link expires ${fmtTime(r.expires_at)}` : ""}{r.approved_at ? ` · approved ${fmtTime(r.approved_at)} by ${r.approved_by}` : ""}</div>
          </div>
          <div><Pill s={r.status} /></div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {r.status === "draft" && <button disabled={busy} className={primary} onClick={() => act(() => api.clientEsign.send(clientId, rid), setSent)}>Send signing link</button>}
          {r.status !== "draft" && r.status !== "cancelled" && <button disabled={busy} className={ghost} onClick={() => window.confirm("Issue a new link and code? The old link stops working.") && act(() => api.clientEsign.send(clientId, rid), setSent)}>Re-send link</button>}
          {r.status === "signed" && <button disabled={busy} className={`${btn} bg-green-700 text-white disabled:opacity-50`} onClick={() => window.confirm("Approve these documents? Signed PDFs will be filed in the client record and become visible to NH DHHS inspectors.") && act(() => api.clientEsign.approve(clientId, rid), () => reloadClient())}>{busy ? "Filing…" : "Approve & file in client record"}</button>}
          {["draft", "sent", "in_progress", "signed", "paper_requested"].includes(r.status) && <button disabled={busy} className={`${btn} text-red-700`} onClick={() => window.confirm("Cancel this signing request? The link stops working.") && act(() => api.clientEsign.cancel(clientId, rid))}>Cancel request</button>}
        </div>
        {sent && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm space-y-2">
            <div className="font-semibold text-blue-900">Signing link ready {sent.email_sent ? "· emailed" : ""}{sent.sms_sent ? " · code texted" : ""}</div>
            <div className="break-all"><span className="text-gray-600">Link:</span> <code>{sent.link}</code> <button className="underline text-[#154E93] ml-1" onClick={() => navigator.clipboard?.writeText(sent.link)}>Copy</button></div>
            <div><span className="text-gray-600">Access code:</span> <b className="text-lg tracking-widest">{sent.code}</b></div>
            <p className="text-xs text-gray-600">{sent.sms_sent ? "The code was texted to the signer." : "Give the code to the signer by phone or in person — not in the same email as the link."} It is shown only once; use Re-send to issue a new one.</p>
          </div>
        )}
        {error && <div className="mt-3 text-sm text-red-700">{error}</div>}
        {missing && <ul className="mt-2 list-disc ml-5 text-sm text-amber-900">{missing.map((m) => <li key={m}>{m}</li>)}</ul>}
      </div>

      <div className="bg-white border rounded-xl divide-y">
        {r.docs.map((d) => {
          const doneSigner = d.signer_status === "signed";
          return (
            <div key={d.key}>
              <button className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-slate-50" onClick={() => setOpen(open === d.key ? null : d.key)}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${d.document_id ? "bg-green-600 text-white" : doneSigner ? "bg-amber-400 text-white" : "border-2 border-gray-300"}`}>{d.document_id || doneSigner ? "✓" : ""}</span>
                <span className="flex-1 min-w-[180px]">
                  <span className="font-medium text-gray-800">{d.title}</span>
                  <span className="block text-xs text-gray-500">
                    {d.document_id ? "Filed in client Documents" : doneSigner ? `Signer finished ${fmtTime(d.signer_signed_at)}` : `Signer: ${d.signer_missing.length} item(s) left`}
                    {d.presend_missing.length ? ` · Jybek must complete before sending: ${d.presend_missing.length}` : d.agency_missing.length ? ` · Jybek: ${d.agency_missing.length} item(s) left` : ""}
                  </span>
                </span>
                <span className="text-sm text-[#154E93]">{open === d.key ? "Close" : r.status === "approved" ? "View" : "Open"}</span>
              </button>
              {open === d.key && <AgencyDoc clientId={clientId} req={r} docKey={d.key} onChanged={load} />}
            </div>
          );
        })}
      </div>

      <details className="bg-white border rounded-xl p-4">
        <summary className="font-semibold text-sm cursor-pointer">Audit trail ({r.audit.length})</summary>
        <table className="w-full text-xs mt-3"><tbody>
          {r.audit.map((a) => <tr key={a.id} className="border-t"><td className="py-1 pr-2 whitespace-nowrap">{fmtTime(a.at)}</td><td className="pr-2">{a.actor}</td><td className="pr-2 font-medium">{a.event}</td><td className="pr-2">{a.detail}</td><td className="text-gray-500">{a.ip}</td></tr>)}
        </tbody></table>
      </details>
    </div>
  );
}

export default function ClientEsignTab({ clientId, reload }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [view, setView] = useState(null); // null | "new" | request id
  const [sig, setSig] = useState(null);
  const load = () => { api.clientEsign.list(clientId).then(setInfo).catch((e) => setError(e.message)); api.clientEsign.mySignature(clientId).then(setSig).catch(() => {}); };
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!info) return <div className="text-gray-500">{error || "Loading…"}</div>;

  if (sig && !sig.adopted) return (
    <div className="bg-white border rounded-xl p-5 space-y-3 max-w-2xl">
      <h3 className="font-bold text-lg text-[#1B3A6B]">Adopt your Jybek signature first</h3>
      <p className="text-sm text-gray-600">Your signature is applied where Jybek signs (service agreement, discharge letter). It is the same signature used on New Hire Packets.</p>
      <SignatureAdopt withTitle defaultTitle="Agency Representative" defaultName={sig.name || ""} onAdopt={async (b) => { await api.clientEsign.adoptMySignature(clientId, b); load(); }} submitLabel="Adopt signature" />
    </div>
  );
  if (view === "new") return <NewRequest clientId={clientId} info={info} onCancel={() => setView(null)} onCreated={(id) => { load(); setView(id); }} />;
  if (view) return <RequestDetail clientId={clientId} rid={view} onBack={() => { setView(null); load(); }} reloadClient={() => { load(); reload && reload(); }} />;

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-lg text-[#1B3A6B]">Client e-signatures</h3>
          <p className="text-sm text-gray-600">Send the admission packet or a discharge letter to the client or family representative. Once you approve the signed documents they are filed in the client's Documents and shown to NH DHHS inspectors.</p>
        </div>
        <button className={primary} onClick={() => setView("new")}>+ New signing request</button>
      </div>
      {!info.audit_chain?.ok && <div className="text-sm bg-red-50 border border-red-200 rounded-lg p-3 text-red-800">Warning: the e-signature audit log failed its integrity check. Contact your administrator.</div>}
      <div className="bg-white border rounded-xl divide-y">
        {info.requests.length === 0 && <div className="p-5 text-sm text-gray-500">No signing requests yet.</div>}
        {info.requests.map((r) => (
          <button key={r.id} onClick={() => setView(r.id)} className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-slate-50">
            <span className="flex-1 min-w-[200px]">
              <span className="font-medium text-gray-800">{r.kind === "discharge" ? "Discharge letter" : "Admission documents"} · {r.signer_name}</span>
              <span className="block text-xs text-gray-500">{r.docs.map((d) => d.title).join(", ")} · created {fmtTime(r.created_at)}</span>
            </span>
            <span className="text-xs text-gray-500">{r.docs.filter((d) => d.signer_status === "signed").length}/{r.docs.length} signed</span>
            <Pill s={r.status} />
          </button>
        ))}
      </div>
    </div>
  );
}
